import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Sparkles, ListChecks, Search, Package, ChevronRight, ArrowLeft, CalendarDays, Check, Sun, ChefHat, Edit2, Filter, Tag, ChevronUp, ChevronDown, Wand2 } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import WeekDateSelector from './WeekDateSelector';
import CookingPreferencesSelector from './CookingPreferencesSelector';
import PantrySelector from './PantrySelector';
import { profileAPI, pantryAPI, smartFillAPI, mealPlanAPI, authAPI } from '../utils/api';
import useUserStore from '../store/userStore';
import TasteSetupCards from './TasteSetupCards';
import { toast } from 'sonner';
import { getTodayString, formatDateString } from '../utils/dateUtils';
import { planPathForDates, startFlowState } from '../utils/planLocation';

/**
 * PlanFoodLauncher - Modal for initiating meal planning
 * 
 * Flow: Plan Day → Method → Before We Start → Generate
 * 
 * "Before we start" includes cooking prefs, pantry, and filters inline.
 * Smart Fill generates directly from this modal (no navigation to SmartFill page).
 * Browse/Step-by-step still navigate to their respective pages.
 */
function PlanFoodLauncher({ isOpen, onClose, context = 'home', weekDates, specificDate, onPlanGenerated, presetDate, returnTo, onPresetSmartFill }) {
  var navigate = useNavigate();
  // Where Browse & Choose / Step-by-Step return: the host's exact location
  // (Plan passes its current Daily/Weekly URL; Home passes /home).
  var flowReturn = returnTo || (context === 'home' ? '/home' : '/plan');
  var [step, setStep] = useState(1);
  var [scope, setScope] = useState(null);
  var [method, setMethod] = useState(null);
  var [selectedDays, setSelectedDays] = useState([]);
  
  // Cooking preferences
  var [cookingPrefsLoaded, setCookingPrefsLoaded] = useState(false);
  var [cookingPrefsSet, setCookingPrefsSet] = useState(false);
  var [showCookingSetup, setShowCookingSetup] = useState(false);
  var [editingCooking, setEditingCooking] = useState(false);
  var [selectedCookware, setSelectedCookware] = useState([]);
  var [selectedCookTime, setSelectedCookTime] = useState(null);
  
  // Pantry
  var [pantryItems, setPantryItems] = useState([]);
  var [editingPantry, setEditingPantry] = useState(false);
  
  // Filters
  var [showFiltersPanel, setShowFiltersPanel] = useState(false);
  var [selectedTags, setSelectedTags] = useState([]);
  var [selectedCuisine, setSelectedCuisine] = useState([]);

  // LEFTOVERS-LITE: server flag + per-session opt-in (default OFF), same
  // contract as PlanningHub's "Before You Plan" — eligible only for 2+
  // selected days; a manual toggle sticks until this launcher session closes.
  var [leftoversFlagEnabled, setLeftoversFlagEnabled] = useState(false);
  var [includeLeftovers, setIncludeLeftovers] = useState(false);
  var [leftoversTouched, setLeftoversTouched] = useState(false);

  // Generation state
  var [generating, setGenerating] = useState(false);

  // First-plan taste setup gate (shown ONCE before the normal flow). Read the
  // account-level flag from the store; treat missing as false. `setupDone`
  // flips locally the moment the deck finishes so we continue without a reload.
  var { user, setUser } = useUserStore();
  var [setupDone, setSetupDone] = useState(false);
  // Guard against STALE persisted local user state: when the local store says
  // setup is incomplete/missing we re-verify the account-level flag from the
  // existing /auth/me BEFORE showing the deck. `gateChecked` gates rendering so
  // the deck never flashes before that verification resolves.
  var [gateChecked, setGateChecked] = useState(false);
  var localIncomplete = !!(user && user.user_id) && !user.taste_setup_complete;
  var verifyingGate = !gateChecked && localIncomplete && !setupDone;
  var needsTasteSetup = gateChecked && localIncomplete && !setupDone;

  function handleTasteSetupComplete(marked) {
    // marked === true means the backend flag was set (all cards answered). Reflect
    // it locally so the deck never reappears this session; a load/empty failure
    // passes no arg and simply continues WITHOUT marking complete.
    if (marked && user) {
      setUser(Object.assign({}, user, { taste_setup_complete: true }));
    }
    setSetupDone(true);
  }

  
  var today = getTodayString();
  
  function getCurrentWeekDates() {
    var now = new Date();
    var dayOfWeek = now.getDay();
    var startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - dayOfWeek);
    var dates = [];
    for (var i = 0; i < 7; i++) {
      var d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      dates.push(formatDateString(d));
    }
    return dates;
  }
  
  var effectiveWeekDates = weekDates || getCurrentWeekDates();

  // Load cooking preferences on mount
  useEffect(function() {
    profileAPI.getCookingPreferences()
      .then(function(res) {
        var data = res.data;
        setCookingPrefsSet(data.is_set);
        setSelectedCookware(data.cookware || []);
        setSelectedCookTime(data.max_cook_time);
        setCookingPrefsLoaded(true);
      })
      .catch(function(err) {
        console.error('Failed to load cooking preferences:', err);
        setCookingPrefsLoaded(true);
      });
  }, []);

  // Load pantry items on mount
  useEffect(function() {
    pantryAPI.getPantry()
      .then(function(response) {
        var data = response.data || {};
        var allItems = [];
        Object.keys(data).forEach(function(category) {
          var items = data[category] || [];
          items.forEach(function(item) {
            var itemName = typeof item === 'string' ? item : (item.name || item.display_name || '');
            if (itemName && !allItems.includes(itemName)) {
              allItems.push(itemName);
            }
          });
        });
        setPantryItems(allItems);
      })
      .catch(function(err) {
        console.error('Failed to load pantry from API:', err);
      });
  }, []);

  // LEFTOVERS-LITE: fetch the server feature flag once on mount so "Include
  // leftovers" is only shown/enabled when the backend supports it.
  useEffect(function() {
    var cancelled = false;
    smartFillAPI.getFeatureFlags()
      .then(function(res) {
        if (!cancelled) setLeftoversFlagEnabled(!!(res && res.data && res.data.leftovers_lite_enabled));
      })
      .catch(function() { if (!cancelled) setLeftoversFlagEnabled(false); });
    return function() { cancelled = true; };
  }, []);

  // LEFTOVERS-LITE: default the toggle ON for eligible multi-day selections
  // (2+ days), but never clobber a manual choice made during this session.
  useEffect(function() {
    if (!leftoversTouched) {
      setIncludeLeftovers(leftoversFlagEnabled && selectedDays.length >= 2);
    }
  }, [leftoversFlagEnabled, selectedDays.length, leftoversTouched]);

  // Reset state when modal opens
  useEffect(function() {
    if (isOpen) {
      // Opt-in preset/locked date (e.g. guided Fix Days): the target date is
      // already decided by the caller, so the launcher must skip Step 1's
      // date/scope selection entirely and land on the existing Step 2
      // (method selection) with selectedDays already containing exactly
      // [presetDate]. Every other caller is unaffected (presetDate is
      // undefined/null for normal Home/Daily/Weekly/Custom opens).
      if (presetDate) {
        setStep(2);
        setScope('day');
        setSelectedDays([presetDate]);
      } else {
        setStep(1);
        setScope(null);
        setSelectedDays([]);
      }
      setMethod(null);
      setShowCookingSetup(false);
      setEditingCooking(false);
      setEditingPantry(false);
      setShowFiltersPanel(false);
      setGenerating(false);
      setLeftoversTouched(false);  // new launcher session re-derives the default
      // Re-arm the taste-setup gate each open: a failed/empty attempt (which
      // continues planning WITHOUT persisting taste_setup_complete) must stay
      // eligible to retry on a later Plan Food open. A successful completion
      // persists user.taste_setup_complete=true, so the deck still never
      // reappears in that case regardless of this reset.
      setSetupDone(false);
      // Verify the ACCOUNT-level flag against /auth/me before showing the deck,
      // so a stale local `false` (e.g. account completed setup on another device)
      // does not re-show it. If the local store already says complete we trust it.
      if (!(user && user.user_id) || user.taste_setup_complete) {
        setGateChecked(true);
      } else {
        setGateChecked(false);
        authAPI.getMe()
          .then(function (res) {
            var srv = res && res.data;
            if (srv && srv.taste_setup_complete && user) {
              setUser(Object.assign({}, user, { taste_setup_complete: true }));
            }
          })
          .catch(function () { /* nonfatal: fall back to local state */ })
          .then(function () { setGateChecked(true); });
      }
    }
  }, [isOpen]);  // eslint-disable-line react-hooks/exhaustive-deps

  function formatDateShort(dateStr) {
    var d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  function formatSpecificDate(dateStr) {
    if (!dateStr) return '';
    var d = new Date(dateStr + 'T00:00:00');
    if (dateStr === today) return 'Today';
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  }

  function getWeekRangeLabel() {
    if (!effectiveWeekDates || effectiveWeekDates.length === 0) return '';
    var start = new Date(effectiveWeekDates[0] + 'T00:00:00');
    var end = new Date(effectiveWeekDates[effectiveWeekDates.length - 1] + 'T00:00:00');
    return start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + 
           ' - ' + 
           end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  function handleScopeSelect(selectedScope) {
    setScope(selectedScope);
    if (selectedScope === 'today') {
      setSelectedDays([today]);
      setStep(2);
    } else if (selectedScope === 'day') {
      setSelectedDays([specificDate || today]);
      setStep(2);
    } else if (selectedScope === 'week') {
      // Product Rule 4: "Plan This Week" must never request past dates —
      // effectiveWeekDates is the full Sun-Sat calendar week, which includes
      // earlier days when today isn't Sunday. Scope the request to
      // today-through-end-of-week only. Custom scope is untouched.
      var weekDatesFromToday = (effectiveWeekDates || []).filter(function(d) { return d >= today; });
      setSelectedDays(weekDatesFromToday);
      setStep(2);
    } else if (selectedScope === 'custom') {
      setSelectedDays([]);
    }
  }

  function handleDaysConfirm() {
    if (selectedDays.length > 0) {
      setStep(2);
    }
  }

  function handleMethodSelect(selectedMethod) {
    // Locked-date caller (guided Fix Days) whose date already has saved meals:
    // Smart Fill must not run the blank-day generator here (it can replace
    // pending meals). Hand the date back so the caller opens its existing
    // Regenerate Day flow (Keep any meals? -> draft -> Review -> Save).
    if (selectedMethod === 'smart-fill' && presetDate && onPresetSmartFill) {
      onPresetSmartFill(presetDate);
      handleClose();
      return;
    }

    setMethod(selectedMethod);
    
    if (selectedMethod === 'browse' || selectedMethod === 'step-by-step') {
      // For browse/step-by-step, launch directly (no Before We Start needed)
      launchNonSmartFill(selectedMethod);
      return;
    }
    
    // Smart Fill: Show first-time cooking setup or go to Before We Start
    if (!cookingPrefsSet && cookingPrefsLoaded) {
      setShowCookingSetup(true);
    } else {
      setStep(3); // Go to "Before we start"
    }
  }

  function handleCookingSetupDone() {
    profileAPI.updateCookingPreferences({
      cookware: selectedCookware,
      max_cook_time: selectedCookTime
    }).then(function() {
      setCookingPrefsSet(true);
      setShowCookingSetup(false);
      setStep(3); // Go to "Before we start"
    }).catch(function(err) {
      console.error('Failed to save cooking preferences:', err);
      toast.error('Failed to save cooking preferences');
    });
  }

  function handleSkipCookingSetup() {
    setShowCookingSetup(false);
    setStep(3);
  }

  function handleEditCookingPrefs() {
    setEditingCooking(true);
  }

  function handleSaveCookingPrefs() {
    profileAPI.updateCookingPreferences({
      cookware: selectedCookware,
      max_cook_time: selectedCookTime
    }).then(function() {
      setCookingPrefsSet(true);
      setEditingCooking(false);
      toast.success('Cooking preferences updated');
    }).catch(function(err) {
      console.error('Failed to save preferences:', err);
      toast.error('Failed to save preferences');
    });
  }

  function handleEditPantry() {
    setEditingPantry(true);
  }

  function handleSavePantry() {
    // PantrySelector handles backend save on Done — just close editor
    setEditingPantry(false);
  }

  // Generate meals via Smart Fill API directly
  async function handleGenerate() {
    setGenerating(true);
    
    try {
      var cookingPreferences = {};
      if (selectedCookware.length > 0) {
        cookingPreferences.cookware = selectedCookware;
      }
      if (selectedCookTime) {
        cookingPreferences.max_cook_time = selectedCookTime;
      }
      
      var filters = {};
      if (selectedTags.length > 0) {
        filters.tags = selectedTags;
      }
      if (selectedCuisine.length > 0) {
        filters.cuisine = selectedCuisine;
      }
      
      // LEFTOVERS-LITE: only for multi-day (2+) selections; re-checked here
      // (not just at the UI-gate) so a stale toggle can never leak into a
      // single-day request.
      var leftoversForRequest = includeLeftovers && selectedDays.length >= 2;

      // GENERATE WEEK -> REVIEW -> CONFIRM & SAVE: a multi-day (2+) request is
      // generated as a DRAFT (nothing persisted) and handed to the existing
      // PlanReview contract, which owns Confirm & Save (idempotency,
      // incomplete-date / On Track blocking, leftover pairs, reevaluation).
      // Single-day generation keeps its direct-save behavior below.
      var isWeeklyDraft = selectedDays.length >= 2;

      // Pantry is auto-fetched by backend from user profile
      var res = await smartFillAPI.auto(selectedDays, {}, [], cookingPreferences, filters, [], [], isWeeklyDraft, leftoversForRequest);

      if (isWeeklyDraft) {
        var draft = res && res.data;
        if (draft && draft.success && (draft.added_plans || []).length > 0) {
          var draftDates = selectedDays.slice();
          handleClose();
          navigate('/plan/review', { state: startFlowState({
            returnTo: flowReturn,
            added_plans: draft.added_plans,
            // Real target + goal from the fail-closed draft response — never
            // a fabricated fallback (PlanReview refuses state without them).
            daily_target: draft.daily_target || draft.summary,
            daily_results: draft.daily_results,
            replace_plan_ids: draft.replace_plan_ids || [],
            dates: draftDates,
            goal: draft.goal,
            weekOffset: 0,
            // Cancel returns to the origin (returnTo); a successful Confirm &
            // Save opens the generated week itself in Plan.
            saveReturnTo: planPathForDates(draftDates),
          }) });
        } else if (draft && draft.success) {
          toast.info("No meals could be generated for the selected days. Try removing a filter, then generate again.");
        } else {
          toast.error((draft && draft.message) || 'Failed to generate meals');
        }
        return;
      }

      if (res.data && res.data.success !== false) {
        var addedCount = (res.data.added_plans || []).length;
        toast.success(addedCount + ' meal' + (addedCount !== 1 ? 's' : '') + ' generated');
        
        // Notify parent (with the date(s) actually generated — see
        // PlanningHub's onPlanGenerated for why this matters for Fix Days)
        // and close.
        if (onPlanGenerated) {
          onPlanGenerated(selectedDays);
        }
        handleClose();
        
        // Show what was just planned: that day in Daily, or the Weekly week of
        // the earliest planned day (a future/custom week, not the current one).
        // Inside Plan this replaces the current entry; from Home it navigates.
        navigate(planPathForDates(selectedDays), { replace: context !== 'home' });
      } else {
        toast.error(res.data?.message || 'Failed to generate meals');
      }
    } catch (err) {
      console.error('Smart fill error:', err);
      // For 402 subscription/limit/planning errors the global axios
      // interceptor (api.js) already routed the user to the paywall or
      // ProUpgradeNotice. Showing an extra "Failed to generate meals" toast
      // on top of that is the "glitchy failed state" users complained about.
      // Close the launcher quietly and let the global handler own the UX.
      var status = err?.response?.status;
      var code = err?.response?.data?.detail?.error || err?.response?.data?.error;
      if (
        status === 402 &&
        (code === 'subscription_required' ||
          code === 'daily_limit_reached' ||
          code === 'planning_around_logged_meals_requires_pro')
      ) {
        handleClose();
        return;
      }
      toast.error('Failed to generate meals. Please try again.');
    } finally {
      setGenerating(false);
    }
  }

  // Launch non-smart-fill methods (browse, step-by-step)
  function launchNonSmartFill(selectedMethod) {
    var dates = selectedDays;
    var params = new URLSearchParams();
    
    if (selectedCookware.length > 0) {
      params.set('cookware', selectedCookware.join(','));
    }
    if (selectedCookTime) {
      params.set('maxCookTime', selectedCookTime.toString());
    }

    if (selectedMethod === 'step-by-step') {
      params.set('guided', 'true');
      params.set('return', flowReturn);
      params.set('dates', dates.join(','));
      navigate('/plan/guided?' + params.toString(), { state: startFlowState() });
    } else if (selectedMethod === 'browse') {
      // Browse & Choose adds meals to ONE date. A multi-day scope has no
      // single date (it isn't offered in Step 2) — never fall back to today.
      if (dates.length !== 1) {
        toast.error('Browse & Choose plans one day at a time. Pick a single day.');
        return;
      }
      params.set('guided', 'true');
      params.set('return', flowReturn);
      params.set('date', dates[0]);
      navigate('/meals?' + params.toString(), { state: startFlowState() });
    }

    onClose();
    resetState();
  }

  function resetState() {
    setStep(1);
    setScope(null);
    setMethod(null);
    setSelectedDays([]);
    setShowCookingSetup(false);
    setEditingCooking(false);
    setEditingPantry(false);
    setShowFiltersPanel(false);
    setGenerating(false);
    setLeftoversTouched(false);  // session ended — next open re-derives the default
  }

  function goBack() {
    if (editingPantry) {
      setEditingPantry(false);
      return;
    }
    if (editingCooking) {
      setEditingCooking(false);
      return;
    }
    if (showCookingSetup) {
      setShowCookingSetup(false);
      setStep(2);
      return;
    }
    if (step === 3) {
      setStep(2);
    } else if (step === 2) {
      if (presetDate) {
        // Locked-date launcher (e.g. guided Fix Days): Step 1's date/scope
        // selection must never be revealed. Close instead, which returns the
        // user to whatever already shows the locked target date behind it.
        handleClose();
        return;
      }
      setStep(1);
      setScope(null);
      setSelectedDays([]);
    }
  }

  function handleClose() {
    onClose();
    resetState();
  }

  // ===================== RENDER FUNCTIONS =====================

  function renderCustomDaysPicker() {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground text-center">Select the days you want to plan:</p>
        <WeekDateSelector
          selectedDates={selectedDays}
          onDatesChange={setSelectedDays}
          mode="multiple"
          allowRange={true}
          showApplyButton={false}
        />
        <div className="flex gap-2 pt-2">
          <Button
            onClick={function() { setScope(null); setSelectedDays([]); }}
            variant="outline"
            className="flex-1 rounded-full"
          >
            Back
          </Button>
          <Button
            onClick={handleDaysConfirm}
            disabled={selectedDays.length === 0}
            className="flex-1 rounded-full bg-accent text-white"
            data-testid="confirm-days-btn"
          >
            Continue ({selectedDays.length})
          </Button>
        </div>
      </div>
    );
  }

  function renderAnotherDayPicker() {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground text-center">Select a day to plan:</p>
        <WeekDateSelector
          selectedDates={selectedDays}
          onDatesChange={setSelectedDays}
          mode="single"
          allowRange={false}
          showApplyButton={false}
        />
        <div className="flex gap-2 pt-2">
          <Button
            onClick={function() { setScope(null); setSelectedDays([]); }}
            variant="outline"
            className="flex-1 rounded-full"
          >
            Back
          </Button>
          <Button
            onClick={handleDaysConfirm}
            disabled={selectedDays.length === 0}
            className="flex-1 rounded-full bg-accent text-white"
            data-testid="confirm-another-day-btn"
          >
            Continue
          </Button>
        </div>
      </div>
    );
  }

  // Render filters panel (shared between Before We Start pages)
  function renderFiltersInline() {
    return (
      <div className="border border-border/40 rounded-xl overflow-hidden">
        <button
          onClick={function() { setShowFiltersPanel(!showFiltersPanel); }}
          className="w-full p-4 bg-secondary/30 hover:bg-secondary/50 transition-colors"
          data-testid="toggle-filters-btn"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
              <Filter size={20} className="text-muted-foreground" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium">Meal Filters</p>
              <p className="text-xs text-muted-foreground">
                {(selectedTags.length > 0 || selectedCuisine.length > 0)
                  ? (selectedTags.length > 0 ? selectedTags.length + ' tags' : '') + 
                    (selectedTags.length > 0 && selectedCuisine.length > 0 ? ' \u2022 ' : '') +
                    (selectedCuisine.length > 0 ? selectedCuisine.length + ' cuisine' : '')
                  : 'Filter by tags, cuisine (optional)'}
              </p>
            </div>
            {showFiltersPanel ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </button>
        
        {showFiltersPanel && (
          <div className="p-4 pt-2 border-t border-border/40 space-y-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                <Tag size={12} /> Tags
              </p>
              <div className="flex flex-wrap gap-1.5">
                {['High-Protein', 'Low-Carb', 'Quick', 'Vegetarian', 'Gluten-Free', 'Dairy-Free'].map(function(tag) {
                  var isSelected = selectedTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      onClick={function() {
                        if (isSelected) {
                          setSelectedTags(selectedTags.filter(function(t) { return t !== tag; }));
                        } else {
                          setSelectedTags([].concat(selectedTags, [tag]));
                        }
                      }}
                      className={'px-2.5 py-1 rounded-full text-xs font-medium transition-colors ' + 
                        (isSelected ? 'bg-accent text-white' : 'bg-secondary/50 hover:bg-secondary/70')}
                      data-testid={'tag-' + tag}
                    >
                      {isSelected && <Check size={10} className="inline mr-1" />}
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Cuisine</p>
              <div className="flex flex-wrap gap-1.5">
                {['Mexican', 'Italian', 'Asian', 'American', 'Mediterranean'].map(function(cuisine) {
                  var isSelected = selectedCuisine.includes(cuisine);
                  return (
                    <button
                      key={cuisine}
                      onClick={function() {
                        if (isSelected) {
                          setSelectedCuisine(selectedCuisine.filter(function(c) { return c !== cuisine; }));
                        } else {
                          setSelectedCuisine([].concat(selectedCuisine, [cuisine]));
                        }
                      }}
                      className={'px-2.5 py-1 rounded-full text-xs font-medium transition-colors ' + 
                        (isSelected ? 'bg-purple-500 text-white' : 'bg-secondary/50 hover:bg-secondary/70')}
                      data-testid={'cuisine-' + cuisine}
                    >
                      {isSelected && <Check size={10} className="inline mr-1" />}
                      {cuisine}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {(selectedTags.length > 0 || selectedCuisine.length > 0) && (
              <button
                onClick={function() { setSelectedTags([]); setSelectedCuisine([]); }}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  function renderStepContent() {
    // First-time cooking setup
    if (showCookingSetup) {
      return (
        <div className="space-y-4">
          <h3 className="font-heading font-semibold text-lg text-center">Set up your kitchen</h3>
          <p className="text-sm text-muted-foreground text-center">
            Help us find meals that work with your equipment and schedule
          </p>
          <CookingPreferencesSelector
            selectedCookware={selectedCookware}
            onCookwareChange={setSelectedCookware}
            selectedCookTime={selectedCookTime}
            onCookTimeChange={setSelectedCookTime}
            showDoneButton={false}
            compact={true}
          />
          <div className="flex gap-2 pt-2">
            <Button onClick={handleSkipCookingSetup} variant="outline" className="flex-1 rounded-full">
              Skip
            </Button>
            <Button onClick={handleCookingSetupDone} className="flex-1 rounded-full bg-accent text-white" data-testid="save-cooking-prefs-btn">
              Save & Continue
            </Button>
          </div>
        </div>
      );
    }
    
    // Editing cooking preferences
    if (editingCooking) {
      return (
        <div className="space-y-4">
          <h3 className="font-heading font-semibold text-lg text-center">Edit cooking preferences</h3>
          <CookingPreferencesSelector
            selectedCookware={selectedCookware}
            onCookwareChange={setSelectedCookware}
            selectedCookTime={selectedCookTime}
            onCookTimeChange={setSelectedCookTime}
            showDoneButton={false}
            compact={true}
          />
          <div className="flex gap-2 pt-2">
            <Button onClick={function() { setEditingCooking(false); }} variant="outline" className="flex-1 rounded-full">
              Cancel
            </Button>
            <Button onClick={handleSaveCookingPrefs} className="flex-1 rounded-full bg-accent text-white" data-testid="save-cooking-edit-btn">
              Save
            </Button>
          </div>
        </div>
      );
    }
    
    // Editing pantry
    if (editingPantry) {
      return (
        <div className="space-y-4">
          <h3 className="font-heading font-semibold text-lg text-center">Edit pantry</h3>
          <div className="max-h-[50vh] overflow-y-auto -mx-4 px-4">
            <PantrySelector
              selectedItems={pantryItems}
              onItemsChange={setPantryItems}
              onClose={handleSavePantry}
            />
          </div>
        </div>
      );
    }
    
    // ===== STEP 1: Choose day/scope =====
    if (step === 1) {
      if (context === 'home') {
        return (
          <div className="space-y-4">
            <h3 className="font-heading font-semibold text-lg text-center">Plan your meals</h3>
            {scope !== 'custom' ? (
              <div className="space-y-3">
                <button onClick={function() { handleScopeSelect('today'); }} className="w-full p-4 bg-accent/10 hover:bg-accent/20 border-2 border-accent/30 rounded-xl transition-colors" data-testid="scope-today">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center"><Sun size={20} className="text-white" /></div>
                    <div className="flex-1 text-left">
                      <p className="font-semibold text-foreground">Plan for today</p>
                      <p className="text-xs text-muted-foreground">{formatSpecificDate(today)}</p>
                    </div>
                    <ChevronRight size={18} className="text-accent" />
                  </div>
                </button>
                <button onClick={function() { handleScopeSelect('week'); }} className="w-full p-4 bg-secondary/30 hover:bg-secondary/50 rounded-xl transition-colors" data-testid="scope-week">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center"><Calendar size={20} className="text-muted-foreground" /></div>
                    <div className="flex-1 text-left">
                      <p className="font-medium">Plan for this week</p>
                      <p className="text-xs text-muted-foreground">{getWeekRangeLabel()}</p>
                    </div>
                    <ChevronRight size={18} className="text-muted-foreground" />
                  </div>
                </button>
                <button onClick={function() { handleScopeSelect('custom'); }} className="w-full p-4 bg-secondary/30 hover:bg-secondary/50 rounded-xl flex items-center gap-3 transition-colors" data-testid="scope-custom">
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center"><ListChecks size={20} className="text-muted-foreground" /></div>
                  <div className="flex-1 text-left">
                    <p className="font-medium">Custom</p>
                    <p className="text-xs text-muted-foreground">Pick specific days</p>
                  </div>
                  <ChevronRight size={18} className="text-muted-foreground" />
                </button>
              </div>
            ) : (
              renderCustomDaysPicker()
            )}
          </div>
        );
      }
      
      if (context === 'daily') {
        var displayDate = specificDate || today;
        var isViewingToday = displayDate === today;
        return (
          <div className="space-y-4">
            <h3 className="font-heading font-semibold text-lg text-center">Plan your meals</h3>
            {scope !== 'custom' && scope !== 'another-day' ? (
              <div className="space-y-3">
                <button onClick={function() { handleScopeSelect('day'); }} className="w-full p-4 bg-accent/10 hover:bg-accent/20 border-2 border-accent/30 rounded-xl transition-colors" data-testid="scope-day">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center"><Sun size={20} className="text-white" /></div>
                    <div className="flex-1 text-left">
                      <p className="font-semibold text-foreground">{isViewingToday ? 'Plan for today' : 'Plan for this day'}</p>
                      <p className="text-xs text-muted-foreground">{formatSpecificDate(displayDate)}</p>
                    </div>
                    <ChevronRight size={18} className="text-accent" />
                  </div>
                </button>
                <button onClick={function() { setScope('another-day'); }} className="w-full p-4 bg-secondary/30 hover:bg-secondary/50 rounded-xl flex items-center gap-3 transition-colors" data-testid="scope-another-day">
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center"><CalendarDays size={20} className="text-muted-foreground" /></div>
                  <div className="flex-1 text-left">
                    <p className="font-medium">Plan for another day</p>
                    <p className="text-xs text-muted-foreground">Choose a different date</p>
                  </div>
                  <ChevronRight size={18} className="text-muted-foreground" />
                </button>
                <button onClick={function() { handleScopeSelect('custom'); }} className="w-full p-4 bg-secondary/30 hover:bg-secondary/50 rounded-xl flex items-center gap-3 transition-colors" data-testid="scope-custom">
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center"><ListChecks size={20} className="text-muted-foreground" /></div>
                  <div className="flex-1 text-left">
                    <p className="font-medium">Custom</p>
                    <p className="text-xs text-muted-foreground">Pick multiple specific days</p>
                  </div>
                  <ChevronRight size={18} className="text-muted-foreground" />
                </button>
              </div>
            ) : scope === 'another-day' ? (
              renderAnotherDayPicker()
            ) : (
              renderCustomDaysPicker()
            )}
          </div>
        );
      }
      
      if (context === 'weekly') {
        return (
          <div className="space-y-4">
            <h3 className="font-heading font-semibold text-lg text-center">Plan your meals</h3>
            {scope !== 'custom' ? (
              <div className="space-y-3">
                <button onClick={function() { handleScopeSelect('week'); }} className="w-full p-4 bg-accent/10 hover:bg-accent/20 border-2 border-accent/30 rounded-xl transition-colors" data-testid="scope-week">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center"><Calendar size={20} className="text-white" /></div>
                    <div className="flex-1 text-left">
                      <p className="font-semibold text-foreground">Plan for this week</p>
                      <p className="text-xs text-muted-foreground">{getWeekRangeLabel()}</p>
                    </div>
                    <ChevronRight size={18} className="text-accent" />
                  </div>
                  <div className="mt-3 pt-3 border-t border-accent/20">
                    <div className="flex justify-between">
                      {(effectiveWeekDates || []).map(function(dateStr) {
                        var d = new Date(dateStr + 'T00:00:00');
                        var isDayToday = dateStr === today;
                        return (
                          <div key={dateStr} className={'text-center px-1 ' + (isDayToday ? 'text-accent font-semibold' : 'text-muted-foreground')}>
                            <p className="text-xs">{d.toLocaleDateString('en-US', { weekday: 'narrow' })}</p>
                            <p className="text-sm">{d.getDate()}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </button>
                <button onClick={function() { handleScopeSelect('custom'); }} className="w-full p-4 bg-secondary/30 hover:bg-secondary/50 rounded-xl flex items-center gap-3 transition-colors" data-testid="scope-custom">
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center"><ListChecks size={20} className="text-muted-foreground" /></div>
                  <div className="flex-1 text-left">
                    <p className="font-medium">Custom</p>
                    <p className="text-xs text-muted-foreground">Pick specific days or another week</p>
                  </div>
                  <ChevronRight size={18} className="text-muted-foreground" />
                </button>
              </div>
            ) : (
              renderCustomDaysPicker()
            )}
          </div>
        );
      }
      
      return renderCustomDaysPicker();
    }

    // ===== STEP 2: Choose method =====
    if (step === 2) {
      return (
        <div className="space-y-4">
          <h3 className="font-heading font-semibold text-lg text-center">How would you like to plan?</h3>
          <p className="text-xs text-muted-foreground text-center">
            Planning {selectedDays.length} day{selectedDays.length !== 1 ? 's' : ''}
          </p>
          <div className="space-y-2">
            <button onClick={function() { handleMethodSelect('smart-fill'); }} className="w-full p-4 bg-accent/10 hover:bg-accent/20 border border-accent/30 rounded-xl flex items-center gap-3 transition-colors" data-testid="method-smart-fill">
              <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center"><Sparkles size={20} className="text-white" /></div>
              <div className="flex-1 text-left">
                <p className="font-medium">Smart Fill</p>
                <p className="text-xs text-muted-foreground">AI picks meals to hit your macros</p>
              </div>
              <ChevronRight size={18} className="text-accent" />
            </button>
            <button onClick={function() { handleMethodSelect('step-by-step'); }} className="w-full p-4 bg-secondary/30 hover:bg-secondary/50 rounded-xl flex items-center gap-3 transition-colors" data-testid="method-step-by-step">
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center"><ListChecks size={20} className="text-muted-foreground" /></div>
              <div className="flex-1 text-left">
                <p className="font-medium">Plan Step-by-Step</p>
                <p className="text-xs text-muted-foreground">Pick each meal one at a time</p>
              </div>
              <ChevronRight size={18} className="text-muted-foreground" />
            </button>
            {/* Browse & Choose adds to one date, so it is single-day only. */}
            <button
              onClick={function() { if (selectedDays.length === 1) handleMethodSelect('browse'); }}
              disabled={selectedDays.length !== 1}
              className={'w-full p-4 bg-secondary/30 rounded-xl flex items-center gap-3 transition-colors ' +
                (selectedDays.length === 1 ? 'hover:bg-secondary/50' : 'opacity-50 cursor-not-allowed')}
              data-testid="method-browse"
            >
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center"><Search size={20} className="text-muted-foreground" /></div>
              <div className="flex-1 text-left">
                <p className="font-medium">Browse & Choose</p>
                <p className="text-xs text-muted-foreground">
                  {selectedDays.length === 1 ? 'Explore all meals freely' : 'Available when planning a single day'}
                </p>
              </div>
              <ChevronRight size={18} className="text-muted-foreground" />
            </button>
          </div>
        </div>
      );
    }

    // ===== STEP 3: Before we start (Smart Fill only) =====
    if (step === 3) {
      if (generating) {
        return (
          <div className="py-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
              <div className="animate-spin w-8 h-8 border-3 border-accent border-t-transparent rounded-full" />
            </div>
            <h3 className="font-heading font-bold text-lg mb-2">Generating your plan...</h3>
            <p className="text-sm text-muted-foreground">
              Finding the best meals for {selectedDays.length === 1 ? 'your day' : 'your ' + selectedDays.length + ' days'}
            </p>
          </div>
        );
      }
      
      return (
        <div className="space-y-3">
          <h3 className="font-heading font-bold text-lg text-center mb-1">Before we start</h3>
          <p className="text-sm text-muted-foreground text-center mb-4">
            Review your preferences or generate right away
          </p>
          
          {/* Cooking Preferences */}
          <button
            onClick={handleEditCookingPrefs}
            className="w-full p-4 bg-secondary/30 hover:bg-secondary/50 rounded-xl transition-colors"
            data-testid="edit-cooking-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                <ChefHat size={20} className="text-muted-foreground" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium">Cooking Preferences</p>
                <p className="text-xs text-muted-foreground">
                  {selectedCookware.length > 0 
                    ? selectedCookware.join(', ') + (selectedCookTime ? ' \u2022 ' + selectedCookTime + ' min max' : '')
                    : 'Not set - tap to configure'}
                </p>
              </div>
              <Edit2 size={16} className="text-muted-foreground" />
            </div>
          </button>
          
          {/* My Pantry */}
          <button
            onClick={handleEditPantry}
            className="w-full p-4 bg-secondary/30 hover:bg-secondary/50 rounded-xl transition-colors"
            data-testid="edit-pantry-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                <Package size={20} className="text-muted-foreground" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium">My Pantry</p>
                <p className="text-xs text-muted-foreground">
                  {pantryItems.length > 0 
                    ? pantryItems.length + ' items - auto-prioritized'
                    : 'No items - tap to add'}
                </p>
              </div>
              <Edit2 size={16} className="text-muted-foreground" />
            </div>
          </button>
          
          {/* Meal Filters */}
          {renderFiltersInline()}

          {/* LEFTOVERS-LITE: Include leftovers (multi-day plans only) */}
          {leftoversFlagEnabled && selectedDays.length >= 2 && (
            <button
              type="button"
              onClick={function() { setLeftoversTouched(true); setIncludeLeftovers(function(v) { return !v; }); }}
              className="w-full p-4 bg-secondary/30 hover:bg-secondary/50 rounded-xl transition-colors text-left"
              data-testid="toggle-include-leftovers-btn"
              aria-pressed={includeLeftovers}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center shrink-0">
                  <Package size={20} className="text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">Include leftovers</p>
                  <p className="text-xs text-muted-foreground">
                    Plan select dinners with an extra serving for lunch the next day.
                  </p>
                </div>
                <div
                  className={'relative w-11 h-6 rounded-full transition-colors shrink-0 ' +
                    (includeLeftovers ? 'bg-accent' : 'bg-secondary')}
                >
                  <div
                    className={'absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ' +
                      (includeLeftovers ? 'left-[22px]' : 'left-0.5')}
                  />
                </div>
              </div>
            </button>
          )}

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            className="w-full h-12 rounded-xl bg-accent text-white font-semibold hover:bg-accent/90 mt-4"
            data-testid="generate-plan-btn"
          >
            <Wand2 size={18} className="mr-2" />
            Generate {selectedDays.length === 1 ? 'Plan' : selectedDays.length + ' Days'}
          </Button>
          
          <Button
            onClick={function() { setStep(2); }}
            variant="ghost"
            className="w-full h-10 rounded-xl text-muted-foreground"
          >
            Back
          </Button>
        </div>
      );
    }
  }

  if (!isOpen) return null;

  // Determine step count for indicator
  var totalSteps = method === 'smart-fill' ? 3 : 2;
  var currentDisplayStep = step;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        // z-[55]: above BottomNav (z-50, rendered after this modal on Home/Plan)
        // and below ProUpgradeNotice (z-[60]) which must still win on a 402.
        className="fixed inset-0 z-[55] flex items-center justify-center px-4 launcher-overlay-safe bg-black/50 backdrop-blur-sm"
        data-testid="plan-food-launcher-overlay"
        onClick={generating ? undefined : handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={function(e) { e.stopPropagation(); }}
          className="w-full max-w-md bg-background rounded-2xl shadow-xl overflow-x-hidden overflow-y-auto overscroll-contain launcher-sheet-max-h"
          data-testid="plan-food-launcher"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border/40">
            {(step > 1 || editingCooking || editingPantry || showCookingSetup) && !generating ? (
              <button onClick={goBack} className="p-2 -ml-2 rounded-full hover:bg-secondary transition-colors" data-testid="launcher-back">
                <ArrowLeft size={18} />
              </button>
            ) : (
              <div className="w-9"></div>
            )}
            
            <div className="flex items-center gap-1">
              {Array.from({ length: totalSteps }, function(_, i) {
                return (
                  <div
                    key={i}
                    className={'w-2 h-2 rounded-full transition-colors ' + 
                      (i + 1 === currentDisplayStep ? 'bg-accent' : i + 1 < currentDisplayStep ? 'bg-accent/50' : 'bg-secondary')}
                  />
                );
              })}
            </div>
            
            {!generating ? (
              <button onClick={handleClose} className="p-2 -mr-2 rounded-full hover:bg-secondary transition-colors" data-testid="launcher-close">
                <X size={18} />
              </button>
            ) : (
              <div className="w-9"></div>
            )}
          </div>

          {/* Content */}
          <div className="p-4">
            {verifyingGate ? (
              <div className="py-10 text-center" data-testid="taste-setup-gate-verifying">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent/10 flex items-center justify-center">
                  <div className="animate-spin w-6 h-6 border-3 border-accent border-t-transparent rounded-full" />
                </div>
                <p className="text-sm text-muted-foreground">Getting things ready...</p>
              </div>
            ) : needsTasteSetup ? (
              <TasteSetupCards onComplete={handleTasteSetupComplete} />
            ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={step + '-' + scope + '-' + editingCooking + '-' + editingPantry + '-' + generating}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {renderStepContent()}
              </motion.div>
            </AnimatePresence>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default PlanFoodLauncher;
